import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search as SearchIcon, Chevron, Inbox, AlertTriangle } from "./Icons";

/* ── Page header ──────────────────────────────────────────────────────── */
/* `under` sits below the title on the LEFT, for settings that belong with the
   heading rather than off in the actions slot on the right. */
export function PageHeader({ title, subtitle, under, children }) {
  return (
    <div className="page-head">
      <div>
        <h1 className="page-title">{title}</h1>
        {subtitle && <div className="page-sub">{subtitle}</div>}
        {under && <div className="page-under">{under}</div>}
      </div>
      {children && <div className="page-actions">{children}</div>}
    </div>
  );
}

/* ── Card ─────────────────────────────────────────────────────────────── */
export function Card({ title, note, actions, children, flush = false, className = "" }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="card-head">
          <div className="card-title">
            {title}
            {note && <span className="card-title-note">{note}</span>}
          </div>
          {actions && <div className="row">{actions}</div>}
        </header>
      )}
      <div className={flush ? "card-body--flush" : "card-body"}>{children}</div>
    </section>
  );
}

/* ── Collapsible section ──────────────────────────────────────────────── */
export function Section({ title, accent, meta, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="card">
      <button
        type="button"
        className="sec-head"
        data-open={open}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="row" style={{ gap: 10, minWidth: 0 }}>
          <i className="sec-rule" style={{ background: accent }} aria-hidden />
          <Chevron className="sec-caret" />
          <span className="card-title">{title}</span>
        </span>
        <span className="card-title-note">{meta}</span>
      </button>
      {open && <div className="card-body--flush">{children}</div>}
    </section>
  );
}

/* ── Stat card ────────────────────────────────────────────────────────── */
export function Stat({ label, value, accent, share, meta, plain = false }) {
  return (
    <div className="stat" style={{ "--stat-accent": accent }}>
      <div className="stat-label" title={label}>{label}</div>
      <div className="stat-value-row">
        <span className={`stat-value${plain ? " stat-value--plain" : ""}`}>{value}</span>
        {share && <span className="stat-share">{share}</span>}
      </div>
      {meta && <div className="stat-meta">{meta}</div>}
    </div>
  );
}

/* ── Form controls ────────────────────────────────────────────────────── */
export function Field({ label, children }) {
  return (
    <div className="field">
      {label && <span className="field-label">{label}</span>}
      {children}
    </div>
  );
}

export function Select({ value, onChange, options, style, ...rest }) {
  return (
    <span className="select-wrap">
      <select
        className="select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={style}
        {...rest}
      >
        {options.map((o) =>
          typeof o === "string"
            ? <option key={o} value={o}>{o}</option>
            : <option key={o.value} value={o.value}>{o.label}</option>
        )}
      </select>
    </span>
  );
}

export function SearchBox({ value, onChange, placeholder = "Search…", width }) {
  return (
    <span className="search">
      <span className="search-icon"><SearchIcon /></span>
      <input
        className="input"
        style={width ? { minWidth: width } : undefined}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button className="search-clear" onClick={() => onChange("")} aria-label="Clear search">×</button>
      )}
    </span>
  );
}

export function Segmented({ value, onChange, options }) {
  return (
    <div className="seg" role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          aria-selected={value === o.value}
          className={`seg-item${value === o.value ? " is-active" : ""}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function ChipToggle({ on, partial, onClick, children, count }) {
  return (
    <button
      type="button"
      className={`chip-toggle${on ? " is-on" : ""}${!on && partial ? " is-partial" : ""}`}
      onClick={onClick}
      aria-pressed={on}
    >
      {children}
      {count != null && <span className="chip-count">{count}</span>}
    </button>
  );
}

/* ── Bars ─────────────────────────────────────────────────────────────── */
export function Meter({ value, max, color = "var(--accent)", title }) {
  const w = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="meter" title={title}>
      <div className="meter-fill" style={{ width: `${w}%`, background: color }} />
    </div>
  );
}

export function SplitBar({ parts, title }) {
  const total = parts.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div className="splitbar" title={title}>
      {total > 0
        ? parts.map((p, i) => (
            <i key={i} style={{ width: `${(p.value / total) * 100}%`, background: p.color }} />
          ))
        : null}
    </div>
  );
}

/* ── Sortable table header ────────────────────────────────────────────── */
export function SortTh({ id, sort, setSort, children, numeric = false, style, className = "" }) {
  const active = sort.key === id;
  return (
    <th
      className={`sortable${numeric ? " num" : ""}${className ? ` ${className}` : ""}`}
      style={style}
      onClick={() =>
        setSort({ key: id, dir: active && sort.dir === "asc" ? "desc" : "asc" })
      }
    >
      {children}
      <span className="sort-caret">{active ? (sort.dir === "asc" ? "▲" : "▼") : ""}</span>
    </th>
  );
}

/** Generic comparator driven by a {key, dir} sort state. */
export const sortRows = (rows, sort, accessors = {}) => {
  if (!sort.key) return rows;
  const get = accessors[sort.key] || ((r) => r[sort.key]);
  const mul = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = get(a), bv = get(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * mul;
    return String(av).localeCompare(String(bv), "en", { numeric: true }) * mul;
  });
};

/* ── Resizable table columns ──────────────────────────────────────────── */
/**
 * Drag-to-resize column widths, keyed by column id so a changing column set
 * (an optional Manager column, edit mode) keeps the widths you set. Remembered
 * per browser.
 *
 * Render a <colgroup> from `widths` and drop a <ColResize> into each <th>.
 * The table needs `table-layout: fixed` for the widths to be authoritative.
 */
export function useColumnWidths(storageKey, columns) {
  const [widths, setWidths] = useState(() => {
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(storageKey) || "{}"); } catch { /* ignore */ }
    return Object.fromEntries(columns.map((c) => [c.id, saved[c.id] || c.width]));
  });

  // Columns can come and go; keep what we have and default the newcomers.
  useEffect(() => {
    setWidths((prev) => {
      const next = Object.fromEntries(columns.map((c) => [c.id, prev[c.id] || c.width]));
      const same = Object.keys(next).length === Object.keys(prev).length &&
                   Object.keys(next).every((k) => next[k] === prev[k]);
      return same ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns.map((c) => c.id).join("|")]);

  const drag = useRef(null);

  const onMouseDown = useCallback((id, e) => {
    e.preventDefault();
    e.stopPropagation();
    drag.current = { id, startX: e.clientX, startW: widths[id] || 100 };
    const move = (me) => {
      // Capture the drag up front: the state updater runs later, by which time
      // mouseup may already have cleared drag.current.
      const d = drag.current;
      if (!d) return;
      const w = Math.max(44, d.startW + (me.clientX - d.startX));
      setWidths((prev) => ({ ...prev, [d.id]: w }));
    };
    const up = () => {
      if (drag.current) {
        setWidths((prev) => {
          try { localStorage.setItem(storageKey, JSON.stringify(prev)); } catch { /* ignore */ }
          return prev;
        });
      }
      drag.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }, [widths, storageKey]);

  const reset = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    setWidths(Object.fromEntries(columns.map((c) => [c.id, c.width])));
  }, [columns, storageKey]);

  return { widths, onMouseDown, reset };
}

export const ColResize = ({ id, onMouseDown }) => (
  <span
    className="col-resize"
    onMouseDown={(e) => onMouseDown(id, e)}
    onClick={(e) => e.stopPropagation()}
    role="separator"
    aria-label="Resize column"
  />
);

/* ── Shadow on the sticky first column once the table scrolls ─────────── */
export function useHorizontalScrollShadow() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => el.classList.toggle("is-scrolled", el.scrollLeft > 2);
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  return ref;
}

/* ── States ───────────────────────────────────────────────────────────── */
export const Loading = ({ label = "Loading…" }) => (
  <div className="state">
    <div className="spinner" />
    <span>{label}</span>
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div className="state state--error">
    <AlertTriangle width={24} height={24} />
    <span className="state-title">Something went wrong</span>
    <span>{message}</span>
    {onRetry && <button className="btn btn--sm" onClick={onRetry}>Try again</button>}
  </div>
);

export const EmptyState = ({ title = "Nothing here", hint }) => (
  <div className="state">
    <Inbox />
    <span className="state-title">{title}</span>
    {hint && <span>{hint}</span>}
  </div>
);
